// Durable, native-only staging for the four local-file upload flows (organiser
// "Group Photos"/"Add Photos", attendee "Submit Photos", guest upload-link
// manual upload) so a job survives the visible WebView being destroyed (app
// swiped away mid-upload). Every method is a no-op (or returns an empty/false
// default) off-native - this module is never on the critical path for the
// PWA/browser, same seam as nativeProvider.js.
//
// Layout under Directory.Data (context.filesDir, app-private, survives
// restarts, cleared only on uninstall):
//   upload_jobs/<jobId>/manifest.json
//   upload_jobs/<jobId>/files/0000, 0001, ...  (staged compressed bytes, one
//     per file - index-named so relativePath's own characters never need to
//     be filesystem-safe)
//
// The manifest is intentionally opaque to native (see FolderSyncForegroundService/
// NativeFolderSyncPlugin's own "dumb shadow" convention for SyncPrefs.Target) -
// only this file and UploadWorkerPage.js (the headless resume route) ever parse
// `flowMeta`/`files`.
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';

const JOBS_ROOT = 'upload_jobs';

function jobDir(jobId) {
  return `${JOBS_ROOT}/${jobId}`;
}
function manifestPath(jobId) {
  return `${jobDir(jobId)}/manifest.json`;
}
function stagedFilePath(jobId, index) {
  return `${jobDir(jobId)}/files/${String(index).padStart(4, '0')}`;
}
function stagedDriveDownloadPath(jobId, index) {
  return `${jobDir(jobId)}/drive/${String(index).padStart(4, '0')}`;
}

function isNative() {
  return Capacitor.isNativePlatform();
}

async function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.onload = () => {
      // dataURL looks like "data:<mime>;base64,<data>" - Filesystem.writeFile
      // wants just the base64 payload when no `encoding` option is passed.
      const commaIndex = String(reader.result).indexOf(',');
      resolve(commaIndex >= 0 ? reader.result.slice(commaIndex + 1) : reader.result);
    };
    reader.readAsDataURL(blob);
  });
}

async function readManifestRaw(jobId) {
  const res = await Filesystem.readFile({ path: manifestPath(jobId), directory: Directory.Data, encoding: Encoding.UTF8 });
  return JSON.parse(res.data);
}

async function writeManifestRaw(jobId, manifest) {
  await Filesystem.writeFile({
    path: manifestPath(jobId),
    directory: Directory.Data,
    encoding: Encoding.UTF8,
    data: JSON.stringify(manifest),
  });
}

// Concurrent read-modify-write calls against the same job's manifest race:
// e.g. runOrganiserUploadBatches fires one `onFileUploaded` per successful S3
// PUT inside a `Promise.all` over the whole batch - none of those markUploaded
// calls are awaited or otherwise serialized by the caller, so call B can read
// the manifest before call A's write has landed, then overwrite it with a
// stale copy that silently reverts A's `uploaded: true` flag. That alone was
// harmless (a reverted flag just meant the file got needlessly re-uploaded on
// a later resume) until markUploaded started also deleting the staged bytes to
// reclaim disk space: now the flag can revert to false while the bytes are
// already gone, so a resume tries to load a staged file that no longer exists
// and the whole resume fails outright - confirmed on a real device as ~49
// consecutive FileNotFoundExceptions from a single 50-file batch. Every
// manifest read-modify-write for a given job is now serialized through this
// queue instead of touching the file directly.
const manifestQueues = new Map();
function withManifestLock(jobId, fn) {
  const prev = manifestQueues.get(jobId) || Promise.resolve();
  const next = prev.then(fn, fn);
  manifestQueues.set(jobId, next.catch(() => {}));
  return next;
}

// `flowMeta` is an opaque bag (upload-link token/identity/sessionId/groupingMode,
// credit-hold id, source mode, etc.) - native never parses it, only stores/returns
// it; UploadWorkerPage.js is the only thing that interprets `flowKind`/`flowMeta`.
//
// `entries` (optional) - the full, original relativePath list this job intends
// to upload, known up front for folder-sourced flows (auto-sync, and any other
// SAF-backed source). Without this, the manifest only ever grew incrementally
// as each file reached stageFile() below - so a job interrupted before most
// files were even compressed (e.g. closed at 78/2000) had NO record at all of
// the other ~1922 still-untouched files on resume; only the lucky few that had
// already been staged-but-not-yet-uploaded were recoverable. Pre-populating a
// `staged:false, uploaded:false` placeholder per entry here means resume can
// recover the ENTIRE job, not just its already-compressed tail - see
// `resumeSource` below for how the not-yet-staged ones get re-resolved.
//
// `resumeSource` (optional) - `{kind:'saf', treeUri}` for a folder-backed job,
// letting a resume re-resolve any placeholder (not-yet-compressed) entry's
// original bytes straight from the SAF folder via NativeFolderSync.resolveFile,
// the same way a fresh run would. Omitted (null) for sources whose originals
// aren't re-resolvable after process death (a plain file-picker/drag-drop
// selection, or an upload-link visitor's submission) - those flows don't pass
// `entries` either, so their manifest still grows incrementally exactly as
// before, and a not-yet-staged file is simply unrecoverable on resume, same as
// today.
export async function createJob({ jobId, flowKind, eventId, flowMeta = {}, totalFiles = 0, entries = null, resumeSource = null }) {
  if (!isNative()) return;
  await Filesystem.mkdir({ path: `${jobDir(jobId)}/files`, directory: Directory.Data, recursive: true }).catch(() => {});
  const files = (entries || []).map((e, index) => ({
    index, relativePath: e.relativePath, staged: false, uploaded: false,
  }));
  await writeManifestRaw(jobId, {
    jobId, flowKind, eventId, flowMeta, totalFiles,
    resumeSource: resumeSource || null,
    createdAt: Date.now(),
    files,
  });
}

// Upserts by relativePath (the final, unique-suffixed name assigned at
// compression time - not knowable before then, so a job with no pre-populated
// placeholders (see createJob's `entries`) grows its manifest incrementally).
//
// `matchOriginalRelativePath` (optional) - when createJob pre-populated a
// placeholder for this file keyed by its ORIGINAL (pre-compression, e.g. SAF)
// relativePath, this is that original path, used to find and update the SAME
// entry (renaming it to the final `relativePath`) instead of creating a
// duplicate the placeholder-matching logic in resumeUploadJob.js would never
// recognize as already-covered.
export async function stageFile(jobId, relativePath, compressedFile, matchOriginalRelativePath = null) {
  if (!isNative()) return;
  // base64-encoding the file doesn't touch the manifest - do it before taking
  // the lock so a slow encode of one file doesn't hold up other concurrent
  // stageFile/markUploaded calls for this same job any longer than necessary.
  const base64 = await blobToBase64(compressedFile).catch(() => null);
  if (base64 == null) return;
  await withManifestLock(jobId, async () => {
    try {
      const manifest = await readManifestRaw(jobId);
      let entry = manifest.files.find((f) => f.relativePath === relativePath);
      if (!entry && matchOriginalRelativePath) {
        entry = manifest.files.find((f) => f.relativePath === matchOriginalRelativePath);
      }
      if (!entry) {
        entry = { index: manifest.files.length, relativePath, staged: false, uploaded: false, mimeType: compressedFile.type || 'image/jpeg' };
        manifest.files.push(entry);
      } else {
        entry.relativePath = relativePath;
      }
      await Filesystem.writeFile({ path: stagedFilePath(jobId, entry.index), directory: Directory.Data, data: base64 });
      entry.staged = true;
      entry.mimeType = compressedFile.type || entry.mimeType;
      await writeManifestRaw(jobId, manifest);
    } catch {
      // Staging is a best-effort durability net, not the upload itself - a
      // failure here must never abort the real (in-memory, already-succeeding)
      // upload loop it's shadowing.
    }
  });
}

// `relativePaths` - array of the paths that just finished uploading (Flow 2's
// batched engine reports several at once; the other three report one at a time).
export async function markUploaded(jobId, relativePaths) {
  if (!isNative() || !relativePaths?.length) return;
  await withManifestLock(jobId, () => markUploadedLocked(jobId, relativePaths));
}

async function markUploadedLocked(jobId, relativePaths) {
  let toReclaim = [];
  try {
    const manifest = await readManifestRaw(jobId);
    const set = new Set(relativePaths);
    manifest.files.forEach((f) => {
      if (set.has(f.relativePath)) {
        f.uploaded = true;
        toReclaim.push(f.index);
      }
    });
    await writeManifestRaw(jobId, manifest);
  } catch {
    // Same best-effort rationale as stageFile - never throw into the real upload loop.
    return;
  }
  // Staging only exists so an interrupted job can resume without recompressing -
  // once a file is confirmed delivered that guarantee is no longer needed, so
  // reclaim its staged (compressed) bytes right away rather than leaving them on
  // disk until the whole job finishes (deleteJob). Without this, a large batch
  // (thousands of photos) held every already-uploaded file's compressed copy on
  // disk for the entire job - confirmed on-device as multiple GB of storage that
  // was already safely delivered and no longer needed.
  await Promise.all(toReclaim.map((index) =>
    Filesystem.deleteFile({ path: stagedFilePath(jobId, index), directory: Directory.Data }).catch(() => {})
  ));
}

export async function readManifest(jobId) {
  if (!isNative()) return null;
  try {
    return await readManifestRaw(jobId);
  } catch {
    return null;
  }
}

// Rebuilds a real File from a staged (already-compressed) entry - used only by
// the headless resume path (UploadWorkerPage.js), never by the normal in-flight
// run (which already holds the live File in memory).
export async function loadStagedFile(jobId, entry) {
  const res = await Filesystem.readFile({ path: stagedFilePath(jobId, entry.index), directory: Directory.Data });
  const blobRes = await fetch(`data:${entry.mimeType || 'image/jpeg'};base64,${res.data}`);
  const blob = await blobRes.blob();
  return new File([blob], entry.relativePath, { type: entry.mimeType || blob.type });
}

export async function deleteJob(jobId) {
  if (!isNative()) return;
  await Filesystem.rmdir({ path: jobDir(jobId), directory: Directory.Data, recursive: true }).catch(() => {});
}

// --- Google Drive download-and-stage phase (Flow 5) ---
// A job whose files come from Drive has two phases before it looks like any
// other upload job: `downloading` (fetch raw bytes from Drive using the
// short-lived access token captured at picker time, staged here so a
// swipe-away can resume mid-download) then `uploading` (the raw bytes are
// fed, unmodified, into the same runOrganiserUploadBatches engine every other
// flow uses - via `stageFile`/`markUploaded` above, completely unchanged - so
// compression only ever happens once). Tracked in
// a separate `driveFiles` array (distinct from `files`) so the two phases'
// bookkeeping never collides.

export async function createDriveJob({ jobId, eventId, flowMeta = {}, driveFiles }) {
  if (!isNative()) return;
  await Filesystem.mkdir({ path: `${jobDir(jobId)}/drive`, directory: Directory.Data, recursive: true }).catch(() => {});
  await Filesystem.mkdir({ path: `${jobDir(jobId)}/files`, directory: Directory.Data, recursive: true }).catch(() => {});
  await writeManifestRaw(jobId, {
    jobId, flowKind: 'drive-download-upload', eventId, flowMeta,
    phase: 'downloading',
    totalFiles: driveFiles.length,
    driveFiles: driveFiles.map((f, index) => ({ index, driveFileId: f.driveFileId, name: f.name, downloaded: false })),
    files: [],
    createdAt: Date.now(),
  });
}

export async function stageDriveDownload(jobId, index, file) {
  if (!isNative()) return;
  const base64 = await blobToBase64(file);
  await Filesystem.writeFile({ path: stagedDriveDownloadPath(jobId, index), directory: Directory.Data, data: base64 });
}

export async function markDriveDownloaded(jobId, index) {
  if (!isNative()) return;
  await withManifestLock(jobId, async () => {
    try {
      const manifest = await readManifestRaw(jobId);
      const entry = manifest.driveFiles.find((f) => f.index === index);
      if (entry) entry.downloaded = true;
      await writeManifestRaw(jobId, manifest);
    } catch {
      // Best-effort, same rationale as stageFile/markUploaded.
    }
  });
}

export async function loadStagedDriveDownload(jobId, entry) {
  const res = await Filesystem.readFile({ path: stagedDriveDownloadPath(jobId, entry.index), directory: Directory.Data });
  const blobRes = await fetch(`data:image/jpeg;base64,${res.data}`);
  const blob = await blobRes.blob();
  return new File([blob], entry.name, { type: blob.type || 'image/jpeg' });
}

// Persists the upload engine's own session identity (uploadId + the S3 staging
// prefix the server assigned it) into the manifest, called once the engine
// mints/learns each value (see runOrganiserUploadBatches' onUploadSessionEstablished
// hook). Without this, a resumed run (same jobId, new runOrganiserUploadBatches
// invocation) had no way to find the ORIGINAL run's upload_id and minted a
// brand new one - which the
// server treats as a completely unrelated upload session with its own empty
// staging prefix, so only the resumed run's own remaining files ever got
// promoted into the gallery/grouping job, silently orphaning (and, via
// reset_staging, sometimes actually deleting) every file the interrupted
// original run had already uploaded. Confirmed on a real device: a 2000-photo
// job interrupted at 722 uploaded resumed and grouped only the ~21 files the
// resume itself re-uploaded.
export async function setUploadSession(jobId, session) {
  if (!isNative()) return;
  await withManifestLock(jobId, async () => {
    try {
      const manifest = await readManifestRaw(jobId);
      manifest.uploadSession = { ...(manifest.uploadSession || {}), ...session };
      await writeManifestRaw(jobId, manifest);
    } catch {
      // Best-effort, same rationale as stageFile/markUploaded - worst case a
      // resume falls back to minting a fresh upload_id, exactly today's behavior.
    }
  });
}

export async function setPhase(jobId, phase) {
  if (!isNative()) return;
  await withManifestLock(jobId, async () => {
    try {
      const manifest = await readManifestRaw(jobId);
      manifest.phase = phase;
      await writeManifestRaw(jobId, manifest);
    } catch {
      // Best-effort, same rationale as stageFile/markUploaded.
    }
  });
}

export async function listJobIds() {
  if (!isNative()) return [];
  try {
    const res = await Filesystem.readdir({ path: JOBS_ROOT, directory: Directory.Data });
    return (res.files || []).map((f) => f.name || f);
  } catch {
    return [];
  }
}
